import React, { CSSProperties, useCallback, useEffect, useState } from "react";

import { css } from "@emotion/react";

import { CSS_COLORS } from "../../styles/styles-css-variables";
import { HeroImageVisualElement } from "./subcomponents/HeroImageVisualElement";
import { HeroImageSwitcher } from "./subcomponents/HeroImageSwitcher";
import { CSS_MQ } from "../../styles/breakpoints";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { HeroImageComponentType } from "@swo/storefront";
import { HeroImageHeadlines } from "./subcomponents/HeroImageHeadlines";
import { usePageContext } from "../../global/page-context";

import cn from "classnames";
import { UITabs } from "../StoriesOverviewComponent/ui-tabs/UITabs";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { HeroImageToggle } from "./subcomponents/HeroImageToggle";
import { useDeviceScreenHeightValue } from "./use-device-screen-height-value";

export function HeroImageComponent(
  props: HeroImageComponentType &
    ContentfulComponentCMSHook & { headlineStyle: "normal" | "bold"; isFirstHeroComponentOnPage: boolean },
) {
  const pageCtx = usePageContext();
  const activePagePath = pageCtx.pageBase.path;

  const [toggleState, setToggleState] = useState<HeroImageComponentType["toggle"]>(null);

  useEffect(() => {
    setToggleState(null);
  }, [activePagePath]);

  const onToggle = useCallback(() => {
    return setToggleState(s => {
      if (!s) {
        return props.toggle;
      } else {
        return undefined;
      }
    });
  }, [props.toggle]);

  const variantClassName: "variant-switcher-on-hero-image" | "variant-switcher-as-tabs" =
    props.layout === "switcher-on-hero-image" ? "variant-switcher-on-hero-image" : "variant-switcher-as-tabs";

  const styleObj = useDeviceScreenHeightValue();

  function getAssets() {
    if (toggleState) {
      return {
        desktopVisualAsset: toggleState.desktopVisualAsset,
        mobileVisualAsset: toggleState.mobileVisualAsset || toggleState.desktopVisualAsset,
      };
    } else {
      return { desktopVisualAsset: props.desktopVisualAsset, mobileVisualAsset: props.mobileVisualAsset };
    }
  }

  const assets = getAssets();

  return (
    <div
      css={styles(Boolean(props.toggle || (props.switcher && activePagePath)))}
      className={cn("hero-image", variantClassName, getComponentCMSClassname(props?.sys?.id), {
        "more-shadow": !!props.callToAction,
      })}
      style={styleObj}
    >
      <HeroImageVisualElement
        key={JSON.stringify(props)}
        desktopVisualAsset={assets.desktopVisualAsset}
        mobileVisualAsset={assets.mobileVisualAsset}
      />

      {props.toggle && (
        <HeroImageToggle
          leftLabel={props.toggle.leftLabel}
          rightLabel={props.toggle.rightLabel}
          onToggle={onToggle}
          toggleValue={toggleState ? "right" : "left"}
        />
      )}

      {props.switcher && activePagePath && (
        <div
          css={css`
            grid-area: 2/1/3/2;
            justify-self: center;
            z-index: 3;

            align-items: flex-start;

            .variant-switcher-on-hero-image & {
              margin-top: 15px; //out of design margin to provide some spacing if header variant with some background colour is used
            }

            .variant-switcher-as-tabs & {
              margin-bottom: 20px;
              ${CSS_MQ.atLeastMd} {
                margin-bottom: 40px;
              }
              margin-left: 0;
              margin-right: 0;

              width: 100%;
              max-width: var(--max-content-width-new);

              overflow-x: hidden;
            }
          `}
        >
          {props.layout === "switcher-on-hero-image" ? (
            <HeroImageSwitcher elements={props.switcher} activePagePath={activePagePath} />
          ) : (
            <UITabs
              elements={props.switcher.map(s => {
                return {
                  label: s.label,
                  link: s.link,
                  isActive: s.link === activePagePath,
                  expectSimilarLayout: true,
                };
              })}
            />
          )}
        </div>
      )}

      <HeroImageHeadlines
        seoHeadline={props.seoHeadline}
        emoHeadline={props.emoHeadline}
        callToAction={props.callToAction}
        headlineStyle={props.headlineStyle}
        description={props.text || undefined}
        disableChevron={props.layout === "switcher-as-tabs"}
        seoHeadlineStyle={props.layout === "switcher-as-tabs" ? "normal" : "light"}
        isFirstHeroComponentOnPage={props.isFirstHeroComponentOnPage}
      />
    </div>
  );
}

const styles = (isToggleOrSwitcher: boolean) => css`
  display: grid;

  &.variant-switcher-on-hero-image {
    grid-template-rows: var(--header-unfixed-height) ${isToggleOrSwitcher ? "minmax(200px, 1fr)" : "1fr"} auto;
  }

  &.variant-switcher-as-tabs {
    grid-template-rows: calc(var(--header-unfixed-height) + 10px) auto 1fr auto;
  }

  grid-template-columns: auto;

  --screen-height: calc(100vh - 60px);
  ${CSS_MQ.atLeastMd} {
    --screen-height: 100vh;
  }
  width: 100%;

  min-height: var(--screen-height);

  color: ${CSS_COLORS.White};

  &:not(.more-shadow) {
    --shadow-height: 20%;
    --alpha: 0.3;
  }

  &.more-shadow {
    --shadow-height: 45%;
    --alpha: 0.45;

    ${CSS_MQ.atLeastMd} {
      --shadow-height: 30%;
      --alpha: 0.4;
    }
  }

  position: relative;
  &:before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    min-height: var(--shadow-height);

    z-index: 3;

    background: linear-gradient(0deg, rgba(0, 0, 0, var(--alpha)), rgba(0, 0, 0, 0));
  }

  .visual {
    grid-area: 1 / 1 / span 3 / 2;
  }

  &.variant-switcher-on-hero-image {
    .header-transparent ~ & > .visual {
      grid-area: 1 / 1 / span 3/2;
    }

    .header-opaque ~ & > .visual {
      grid-area: 2 / 1 / span 2 / 2;
    }
  }

  &.variant-switcher-as-tabs {
    .header-transparent ~ & > .visual {
      grid-area: 3 / 1 / span 2/2;
    }

    .header-opaque ~ & > .visual {
      grid-area: 3 / 1 / span 2 / 2;
    }
  }

  .position-absolute {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }
`;
